/**
 * CabZigo Browser Notification Utility
 * Shows OS-level notifications via Service Worker (like WhatsApp Web)
 * Works even when the admin tab is minimized or in the background.
 */

let swRegistration = null;

/**
 * Register the service worker and request notification permission.
 * Call this once when the admin logs in.
 * @returns {Promise<boolean>} true if notifications are permitted
 */
export async function initAdminNotifications() {
  // Check browser support
  if (!('Notification' in window)) {
    console.warn('[Notify] Browser does not support notifications');
    return false;
  }
  if (!('serviceWorker' in navigator)) {
    console.warn('[Notify] Service Workers not supported');
    return false;
  }

  // Request permission
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }

  if (permission !== 'granted') {
    console.warn('[Notify] Notification permission denied');
    return false;
  }

  // Register service worker
  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    console.log('[Notify] ✅ Service Worker registered');

    // Listen for navigation messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NAVIGATE') {
        window.location.href = event.data.url;
      }
    });

    return true;
  } catch (err) {
    console.error('[Notify] Service Worker registration failed:', err);
    return false;
  }
}

/**
 * Show a browser OS notification for a new booking.
 * Works even when the admin tab is in the background / minimized.
 * @param {Object} bookingData - The bookingCreated socket payload
 */
export async function showBookingNotification(bookingData) {
  const title = '🚖 New Booking Received!';
  const bookingNumber = bookingData.bookingNumber || 'New';
  const from = bookingData.from || 'Pickup';
  const to = bookingData.to || 'Drop';
  const amount = bookingData.totalAmount
    ? `₹${bookingData.totalAmount}`
    : '';

  const body = `#${bookingNumber} | ${from} → ${to}${amount ? ` | ${amount}` : ''}`;

  // Use service worker to show notification (works in background)
  if (swRegistration) {
    try {
      await swRegistration.showNotification(title, {
        body,
        icon: '/logo192.png',
        badge: '/favicon.png',
        tag: bookingData.bookingNumber || `booking-${Date.now()}`,
        renotify: true,
        requireInteraction: false,
        silent: false,
        data: {
          bookingNumber: bookingData.bookingNumber,
          url: '/admin/bookings',
        },
        actions: [
          { action: 'view', title: '📋 View Booking' },
          { action: 'dismiss', title: 'Dismiss' },
        ],
      });
      return;
    } catch (err) {
      console.warn('[Notify] SW notification failed, falling back:', err);
    }
  }

  // Fallback: use Notification API directly (works when tab is open)
  if (Notification.permission === 'granted') {
    const notif = new Notification(title, {
      body,
      icon: '/logo192.png',
      tag: bookingData.bookingNumber || `booking-${Date.now()}`,
      renotify: true,
    });

    // Click → focus or open admin panel
    notif.onclick = () => {
      window.focus();
      window.location.href = '/admin/bookings';
      notif.close();
    };
  }
}

/**
 * Check current notification permission status.
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}
